// @givmo/assistant-panel — a console's ONE assistant control, driven by its scope registry.
//
// A console registers this (through its org-console slot) once, with its declared scopes
// (`assistant-scopes.ts`) and the organization-level config every page shares. On each page it:
//
//   · matches the address against the scopes — no match, no control: the cluster draws exactly what
//     it drew before the assistant existed (the cluster law: an icon renders only where its
//     destination is real);
//   · builds the page's own fields from the matched scope — the audience its turns run under, the
//     context they carry (the page, and the record in view), the chip and prompts, and for a
//     `thread: 'record'` scope the thread's match;
//   · decides whether the panel is open: closed until the person opens it, except on a `defaultOpen`
//     scope on a wide screen, where it opens unless the person closed it there earlier this session;
//   · docks the panel beside the page on a wide screen and publishes the width it takes, so the shell
//     reserves it; on a narrow one it is the right-hand sheet and reserves nothing;
//   · moves focus into a docked panel the person opened (never one a page opened by default), and when
//     a docked panel closes from inside — its close, or Escape — hands focus back to this control,
//     so the keyboard is never dropped to the top of the document;
//   · closes the SHEET (never the docked panel) when the page needs the room it covers — a hand edit
//     opening (`yieldAssistantSheet`);
//   · on a `pageBody` scope, draws only the frame and lets the page draw the body
//     (`AssistantPageBody`).

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { AssistantEntryButton } from './assistant-entry-button';
import { AssistantPanel } from './assistant-panel';
import { ASSISTANT_DOCK_WIDTH, AssistantPanelFrame } from './assistant-panel-frame';
import { testIdPrefix, type AssistantPanelConfig } from './assistant-panel-config';
import { setAssistantPageBodyTarget, subscribeAssistantSheetYield } from './assistant-page-body';
import {
  matchAssistantScope,
  scopeContext,
  type AssistantScope,
  type ResolvedAssistantScope,
} from './assistant-scopes';

/** The console's desktop layout, where the panel docks beside the page (Tailwind's `lg`). */
export const ASSISTANT_DOCK_QUERY = '(min-width: 64rem)';
/** Where the docked panel takes its wider width (Tailwind's `xl`). */
const WIDE_DOCK_QUERY = '(min-width: 80rem)';

/** The fields a scope supplies per page; the host supplies everything else. */
type PageFields =
  | 'wireSurface'
  | 'pageWireSurface'
  | 'entryContextExtra'
  | 'activeSurfaceId'
  | 'threadMatch';

export interface ConsoleAssistantProps {
  /** The console's declared scopes. The control renders only on a page one of them matches. */
  scopes: readonly AssistantScope[];
  /** The address the console is showing. */
  pathname: string;
  /** The organization-level config every page shares. */
  base: Omit<AssistantPanelConfig, PageFields>;
  /** Draw the glyph alone on a phone (see `AssistantEntryButton`). */
  glyphOnPhone?: boolean;
  /** Told the width the docked panel takes beside the page, or `null` when it takes none. */
  onDockChange?: (width: number | null) => void;
}

function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', notify);
      return () => list.removeEventListener('change', notify);
    },
    [query],
  );
  const read = useCallback(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(query).matches,
    [query],
  );
  return useSyncExternalStore(subscribe, read, () => false);
}

/** Whether the console is in its desktop layout, where the panel docks. */
export function useAssistantDocks(): boolean {
  return useMediaQuery(ASSISTANT_DOCK_QUERY);
}

/** Where a person's "I closed it here" lives for the session: per scope, this tab only. */
function closedKey(scopeId: string): string {
  return `givmo.assistant.closed.${scopeId}`;
}

function readClosed(scopeId: string): boolean {
  try {
    return window.sessionStorage.getItem(closedKey(scopeId)) === '1';
  } catch {
    return false;
  }
}

function writeClosed(scopeId: string, closed: boolean): void {
  try {
    if (closed) window.sessionStorage.setItem(closedKey(scopeId), '1');
    else window.sessionStorage.removeItem(closedKey(scopeId));
  } catch {
    // Storage refused (a private window): the choice holds for this page view only.
  }
}

/**
 * The panel's open state on this scope. A `defaultOpen` scope on a wide screen is open unless the
 * person closed it there this session; everywhere else it is open only once the person opens it, and
 * stays as they left it while they move between pages.
 */
function useScopeOpen(
  resolved: ResolvedAssistantScope | null,
  docks: boolean,
): [boolean, (next: boolean) => void] {
  const [manualOpen, setManualOpen] = useState(false);
  const scopeId = resolved?.scope.id ?? null;
  const byDefault = resolved?.scope.defaultOpen === true && docks;
  const [closedHere, setClosedHere] = useState(() =>
    scopeId === null ? false : readClosed(scopeId),
  );
  useEffect(() => {
    setClosedHere(scopeId === null ? false : readClosed(scopeId));
  }, [scopeId]);
  const open = byDefault ? !closedHere : manualOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (byDefault && scopeId !== null) {
        writeClosed(scopeId, !next);
        setClosedHere(!next);
        return;
      }
      setManualOpen(next);
    },
    [byDefault, scopeId],
  );
  return [open, setOpen];
}

export function ConsoleAssistant({
  scopes,
  pathname,
  base,
  glyphOnPhone = false,
  onDockChange,
}: ConsoleAssistantProps) {
  const resolved = useMemo(() => matchAssistantScope(scopes, pathname), [scopes, pathname]);
  const docks = useAssistantDocks();
  const wideDock = useMediaQuery(WIDE_DOCK_QUERY);
  const [open, setOpen] = useScopeOpen(resolved, docks);
  const prefix = testIdPrefix(base);

  const reserves = resolved !== null && docks && open;
  const dockWidth = wideDock ? ASSISTANT_DOCK_WIDTH.xl : ASSISTANT_DOCK_WIDTH.base;
  useEffect(() => {
    onDockChange?.(reserves ? dockWidth : null);
  }, [reserves, dockWidth, onDockChange]);
  useEffect(() => () => onDockChange?.(null), [onDockChange]);

  const config: AssistantPanelConfig | null = useMemo(() => {
    if (resolved === null) return null;
    const { scope, match } = resolved;
    const context = scopeContext(resolved);
    return {
      ...base,
      wireSurface: scope.wireSurface,
      pageWireSurface: scope.pageWireSurface?.(match),
      entryContextExtra: context,
      activeSurfaceId: scope.surfaceId?.(match) ?? '',
      threadMatch: scope.thread === 'record' ? context : undefined,
    };
  }, [resolved, base]);

  const entry = useRef<HTMLButtonElement>(null);
  // Whether the person opened it (rather than the page, by default): only then does it take focus.
  const [askedOpen, setAskedOpen] = useState(false);
  // A hand edit opening on the page: the sheet over a narrow page gets out of its way; docked, the
  // panel covers nothing and stays.
  const sheetOpen = useRef(false);
  sheetOpen.current = open && !docks;
  useEffect(
    () =>
      subscribeAssistantSheetYield(() => {
        if (!sheetOpen.current) return;
        setAskedOpen(false);
        setOpen(false);
      }),
    [setOpen],
  );

  const toggle = useCallback(() => {
    setAskedOpen(!open);
    setOpen(!open);
  }, [open, setOpen]);
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) return;
      setAskedOpen(false);
      // A docked panel unmounts with the focus inside it; give the focus back to the control.
      if (docks) entry.current?.focus();
    },
    [docks, setOpen],
  );

  if (resolved === null || config === null) return null;
  return (
    <>
      <AssistantEntryButton
        ref={entry}
        label={base.vocab.productName}
        onClick={toggle}
        expanded={open}
        testId={`${prefix}-entry`}
        glyphOnPhone={glyphOnPhone}
      />
      {resolved.scope.pageBody === true ? (
        <AssistantPanelFrame
          open={open}
          onOpenChange={handleOpenChange}
          docked={docks}
          focusOnOpen={askedOpen}
          title={base.vocab.productName}
          testId={prefix}
        >
          <div
            ref={setAssistantPageBodyTarget}
            className="flex min-h-0 flex-1 flex-col"
            data-scope={resolved.scope.id}
            data-testid={`${prefix}-page-body`}
          />
        </AssistantPanelFrame>
      ) : (
        <AssistantPanel
          open={open}
          onOpenChange={handleOpenChange}
          config={config}
          docked={docks}
          focusOnOpen={askedOpen}
        />
      )}
    </>
  );
}
